import { Autocomplete, TextField } from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DragEvent
} from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiEdit,
  FiEye,
  FiThumbsUp,
  FiTrash2,
  FiTrello,
  FiUserMinus,
  FiUserPlus
} from "react-icons/fi";
import { TbBellRinging } from "react-icons/tb";
import CreateButton from "../../components/CreateButton";
import InfoBanner from "../../components/InfoBanner";
import { useLocation, useNavigate } from "react-router-dom";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { CompanyInterface, TaskInterface } from "../../common/interfaces";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomTooltip from "../../components/CustomTooltip";
import { TypeTaskScope } from "../../components/enum/taskScopeEnum";
import {
  TASK_COLUMNS,
  TaskStatusLabel,
  TypeTaskStatus
} from "../../components/enum/taskStatusEnum";
import TaskCompletedModal from "../../components/modal/TaskCompletedModal";
import TaskCreateModal from "../../components/modal/TaskCreateModal";
import TaskOverdueModal from "../../components/modal/TaskOverdueModal";
import TaskViewModal from "../../components/modal/TaskViewModal";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ActionButton,
  AssigneeDot,
  AssigneeStack,
  Badge,
  Board,
  Card,
  CardFooter,
  CardIndex,
  CardList,
  CardTop,
  Column,
  CompanyPrompt,
  FreeTag,
  ColumnHeader,
  Container,
  EmptyColumn,
  GhostButton,
  Title,
  Toolbar,
  TaskSearchCard
} from "./task.styled";

/** Faixa junto às bordas da coluna que dispara a rolagem durante o arrasto. */
const AUTO_SCROLL_EDGE = 44;
/** Divisor da rampa: quanto mais perto da beirada, mais rápido rola. */
const AUTO_SCROLL_DAMPING = 3.5;

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** Estado inicial do quadro — evita checagem de undefined em cada render. */
const EMPTY_BOARD: Record<TypeTaskStatus, TaskInterface[]> = {
  [TypeTaskStatus.TODO]: [],
  [TypeTaskStatus.IN_PROGRESS]: [],
  [TypeTaskStatus.DONE]: []
};

/** Iniciais para a bolinha do responsável: primeiro e último nome. */
const initialsOf = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const TaskPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user_id, isAdmin, isManager, isOwner } = useAuth();
  /** Só quem valida trabalho alheio aprova tarefa concluída ou a reabre. */
  const canApprove = isAdmin || isManager || isOwner;

  const [board, setBoard] = useState(EMPTY_BOARD);
  const [overdueCount, setOverdueCount] = useState(0);

  // ADMIN enxerga tarefas de todas as empresas misturadas por padrão — sem
  // isso não dá pra diferenciar de quem é cada tarefa. Exige escolher uma
  // empresa antes de mostrar o quadro, mesmo padrão de FinancialEntries.
  const [companies, setCompanies] = useState<CompanyInterface[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyInterface | null>(null);
  const waitingCompany = isAdmin && !selectedCompany;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCompletedOpen, setIsCompletedOpen] = useState(false);
  const [isOverdueOpen, setIsOverdueOpen] = useState(false);

  const [selectedTask, setSelectedTask] = useState<TaskInterface | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<TaskInterface | null>(null);
  const [taskToView, setTaskToView] = useState<TaskInterface | null>(null);

  // Drag & drop nativo: o card arrastado, a coluna sob o cursor e a posição
  // em que ele entraria (null = no fim da coluna).
  const [draggedTask, setDraggedTask] = useState<TaskInterface | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<TypeTaskStatus | null>(
    null
  );
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  // Coluna que está rolando sozinha e a velocidade atual, lidas pelo laço de
  // animação abaixo. Ficam em ref porque mudam a cada `dragover` e não devem
  // provocar render.
  const autoScroll = useRef<{ el: HTMLElement | null; speed: number }>({
    el: null,
    speed: 0
  });

  const clearDragState = () => {
    setDraggedTask(null);
    setDragOverColumn(null);
    setDropIndex(null);
    autoScroll.current = { el: null, speed: 0 };
  };

  // Rolagem contínua enquanto arrasta: `dragover` dispara em intervalos
  // irregulares, então empurrar o scroll direto nele sai aos trancos. O laço de
  // rAF roda a 60fps e só lê a velocidade que o `dragover` deixou na ref.
  useEffect(() => {
    if (!draggedTask) return;

    let frame = requestAnimationFrame(function tick() {
      const { el, speed } = autoScroll.current;
      if (el && speed) el.scrollTop += speed;
      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [draggedTask]);

  // FLIP: guarda onde cada card estava e, quando o quadro muda, anima do lugar
  // antigo para o novo. Sem isso os cards teleportam ao reordenar.
  const cardPositions = useRef(new Map<number, DOMRect>());

  useLayoutEffect(() => {
    const nodes = document.querySelectorAll<HTMLElement>("[data-task-card]");
    const previous = cardPositions.current;
    const current = new Map<number, DOMRect>();
    const skipAnimation = prefersReducedMotion();

    nodes.forEach((node) => {
      const id = Number(node.dataset.taskCard);
      const rect = node.getBoundingClientRect();
      current.set(id, rect);

      if (skipAnimation) return;

      const before = previous.get(id);
      if (!before) return; // card novo: entra sem deslizar

      const dx = before.left - rect.left;
      const dy = before.top - rect.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      node.animate(
        [
          { transform: `translate(${dx}px, ${dy}px)` },
          { transform: "translate(0, 0)" }
        ],
        { duration: 260, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }
      );
    });

    cardPositions.current = current;
  }, [board]);

  const containerId = "toaster-task-page";
  const { autoSuccess, autoError, autoWarn } = usePageToast(containerId, true);

  const handleHttpError = makeToasterErrorHandler(autoError);

  // Lista de empresas pro seletor do ADMIN — mesma rota usada em FinancialEntries.
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/company/admin")
      .then((r) => setCompanies(r.data.items ?? r.data ?? []))
      .catch((error) => console.error("Erro ao buscar empresas:", error));
  }, [isAdmin]);

  const fetchBoard = useCallback(async () => {
    if (waitingCompany) {
      setBoard(EMPTY_BOARD);
      return;
    }
    try {
      const response = await api.get(`/task/board`, {
        params: { company_id: isAdmin ? selectedCompany?.company_id : undefined }
      });
      setBoard({ ...EMPTY_BOARD, ...response.data });
    } catch (error) {
      console.error("Erro ao buscar quadro de tarefas:", error);
      handleHttpError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingCompany, isAdmin, selectedCompany]);

  const fetchOverdueCount = useCallback(async () => {
    if (waitingCompany) {
      setOverdueCount(0);
      return;
    }
    try {
      const response = await api.get(`/task/check-overdue`, {
        params: { company_id: isAdmin ? selectedCompany?.company_id : undefined }
      });
      setOverdueCount(response.data.total ?? 0);
    } catch {
      // falha silenciosa: o contador é informativo
      setOverdueCount(0);
    }
  }, [waitingCompany, isAdmin, selectedCompany]);

  useEffect(() => {
    fetchBoard();
    fetchOverdueCount();
  }, [fetchBoard, fetchOverdueCount]);

  // A sidebar manda `?overdue=1` ao clicar no sino: já abre a lista das
  // tarefas não realizadas e limpa o parâmetro da URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("overdue") === "1") {
      setIsOverdueOpen(true);
      navigate("/task", { replace: true });
    }
  }, [location.search, navigate]);

  const refreshAll = useCallback(() => {
    fetchBoard();
    fetchOverdueCount();
  }, [fetchBoard, fetchOverdueCount]);

  const handleOpenCreateModal = () => {
    setSelectedTask(null);
    setIsCreateOpen(true);
  };

  const handleOpenEditModal = (task: TaskInterface) => {
    setSelectedTask(task);
    setIsCreateOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateOpen(false);
    setSelectedTask(null);
    refreshAll();
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    try {
      await api.delete(`/task/${taskToDelete.task_id}`);
      autoWarn("Tarefa removida com sucesso!");
      refreshAll();
    } catch (error) {
      console.error("Erro ao remover tarefa:", error);
      handleHttpError(error);
    } finally {
      setTaskToDelete(null);
    }
  };

  /// "Pegar para fazer" / "devolver ao time": só mexe na associação de quem
  /// está logado, nunca na dos outros.
  const handleToggleClaim = async (task: TaskInterface, mine: boolean) => {
    try {
      await api.patch(`/task/${task.task_id}/${mine ? "release" : "claim"}`);
      autoSuccess(
        mine
          ? "Tarefa devolvida ao time."
          : "Tarefa sua! Bom trabalho."
      );
      fetchBoard();
    } catch (error) {
      console.error("Erro ao alterar responsável da tarefa:", error);
      handleHttpError(error);
    }
  };

  /// Aprova a tarefa concluída: some do quadro e passa a valer só na
  /// listagem de "Mostrar Concluídos". Restrito a manager/owner/admin no
  /// backend — o botão nem aparece para quem não tem a permissão.
  const handleApprove = async (task: TaskInterface) => {
    try {
      await api.patch(`/task/${task.task_id}/approve`);
      autoSuccess("Tarefa aprovada! Ela foi para a listagem de concluídas.");
      fetchBoard();
    } catch (error) {
      console.error("Erro ao aprovar tarefa:", error);
      handleHttpError(error);
    }
  };

  const handleDrop = async (status: TypeTaskStatus) => {
    const task = draggedTask;
    const requestedIndex = dropIndex;
    const sameColumn = task?.task_status === status;

    clearDragState();

    if (!task) return;

    const origin = board[status];
    const without = origin.filter((t) => t.task_id !== task.task_id);

    // `dropIndex` foi medido sobre a lista COM o card arrastado. Se ele já
    // estava nesta coluna e saiu de uma posição anterior ao destino, tudo que
    // vem depois anda uma casa para trás.
    let insertAt = requestedIndex ?? without.length;
    if (sameColumn) {
      const oldIndex = origin.findIndex((t) => t.task_id === task.task_id);
      if (oldIndex !== -1 && oldIndex < insertAt) insertAt -= 1;
    }
    insertAt = Math.max(0, Math.min(insertAt, without.length));

    const nextColumn = [...without];
    nextColumn.splice(insertAt, 0, { ...task, task_status: status });

    // Soltou onde já estava: nada a gravar.
    const unchanged =
      sameColumn &&
      nextColumn.length === origin.length &&
      nextColumn.every((t, i) => t.task_id === origin[i].task_id);

    if (unchanged) return;

    // Aplica na hora e só depois confirma no servidor, para o quadro não
    // "piscar" esperando a resposta.
    setBoard((prev) => ({
      ...prev,
      ...(sameColumn
        ? {}
        : {
            [task.task_status]: prev[task.task_status].filter(
              (t) => t.task_id !== task.task_id
            )
          }),
      [status]: nextColumn
    }));

    try {
      await api.patch(`/task/reorder`, {
        task_status: status,
        task_ids: nextColumn.map((t) => t.task_id)
      });

      if (!sameColumn) {
        autoSuccess(`Tarefa movida para "${TaskStatusLabel[status]}".`);
      }

      fetchBoard();
    } catch (error) {
      console.error("Erro ao reordenar tarefas:", error);
      handleHttpError(error);
      fetchBoard(); // desfaz o movimento otimista
    }
  };

  /// Um único handler na coluna decide a posição de destino, medindo o cursor
  /// contra o meio de cada card. Antes cada card calculava a sua e a coluna
  /// tratava o resto como "solta no fim" — só que o vão de 8px entre os cards
  /// não é card, então ao atravessar o vão o evento subia para a coluna e o
  /// marcador pulava para o fim da lista a cada card cruzado.
  const handleColumnDragOver = (
    event: DragEvent<HTMLDivElement>,
    status: TypeTaskStatus
  ) => {
    event.preventDefault();

    const list =
      event.currentTarget.querySelector<HTMLElement>("[data-card-list]");
    const cards = list
      ? Array.from(list.querySelectorAll<HTMLElement>("[data-task-card]"))
      : [];

    let index = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      if (event.clientY < rect.top + rect.height / 2) {
        index = i;
        break;
      }
    }

    // Rola a coluna quando o cursor encosta nas bordas — sem isso não há como
    // soltar um card além dos 6 visíveis. A velocidade sobe conforme o cursor
    // chega perto da beirada, então dá para posicionar com precisão em vez de
    // a lista disparar assim que entra na faixa.
    if (list && list.scrollHeight > list.clientHeight) {
      const rect = list.getBoundingClientRect();
      const fromTop = event.clientY - rect.top;
      const fromBottom = rect.bottom - event.clientY;

      let speed = 0;
      if (fromTop < AUTO_SCROLL_EDGE) {
        speed = -(AUTO_SCROLL_EDGE - fromTop) / AUTO_SCROLL_DAMPING;
      } else if (fromBottom < AUTO_SCROLL_EDGE) {
        speed = (AUTO_SCROLL_EDGE - fromBottom) / AUTO_SCROLL_DAMPING;
      }

      autoScroll.current = { el: list, speed };
    } else {
      autoScroll.current = { el: null, speed: 0 };
    }

    setDragOverColumn(status);
    setDropIndex(index);
  };

  const formatDue = (value: string) =>
    new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });

  return (
    <Container>
      <PageToaster containerId={containerId} />

      <Title>
        Quadro de Tarefas
        <CustomTooltip title={waitingCompany ? "Escolha uma empresa primeiro" : ""}>
          <span>
            <CreateButton
              onClick={handleOpenCreateModal}
              disabled={waitingCompany}
              style={{ marginLeft: "14px" }}
            >
              <FiTrello size={18} /> Criar Tarefa
            </CreateButton>
          </span>
        </CustomTooltip>
      </Title>

      <InfoBanner icon={<FiTrello size={26} />} title="Tarefas">
        Quadro de trabalho interno da equipe (A Fazer / Em Progresso /
        Concluído). Use para organizar afazeres individuais ou do time — não
        é vinculado a cliente nem tem data de compromisso marcada.
      </InfoBanner>

      {isAdmin && (
        <TaskSearchCard>
          <Autocomplete
            size="small"
            options={companies}
            sx={{ minWidth: 260 }}
            getOptionLabel={(opt) => opt.company_name || ""}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            value={selectedCompany}
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                // Sem empresa escolhida o quadro vem vazio de propósito —
                // administrador não deve ver/arrastar tarefas de empresas
                // diferentes misturadas na mesma tela.
              />
            )}
          />
        </TaskSearchCard>
      )}

      <Toolbar>
        <CustomTooltip title={waitingCompany ? "Escolha uma empresa primeiro" : ""}>
          <span>
            <GhostButton onClick={() => setIsCompletedOpen(true)} disabled={waitingCompany}>
              <FiCheckCircle /> Mostrar Concluídos
            </GhostButton>
          </span>
        </CustomTooltip>

        <CustomTooltip title={waitingCompany ? "Escolha uma empresa primeiro" : ""}>
          <span>
            <GhostButton $danger onClick={() => setIsOverdueOpen(true)} disabled={waitingCompany}>
              <TbBellRinging /> Não Realizadas
              {overdueCount > 0 && <Badge>{overdueCount}</Badge>}
            </GhostButton>
          </span>
        </CustomTooltip>
      </Toolbar>

      {waitingCompany ? (
        <CompanyPrompt>
          <FiBriefcase />
          <strong>Escolha uma empresa</strong>
          <p>
            Selecione uma empresa acima para ver o quadro de tarefas dela.
            Tarefas de empresas diferentes não aparecem misturadas.
          </p>
        </CompanyPrompt>
      ) : (
      <Board>
        {TASK_COLUMNS.map((status) => (
          <Column
            key={status}
            $status={status}
            $over={dragOverColumn === status}
            onDragOver={(e) => handleColumnDragOver(e, status)}
            onDragLeave={(e) => {
              // `dragleave` também dispara ao entrar num filho (card). Sem esta
              // checagem o destaque e a marca de destino piscavam o arrasto todo.
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
                return;
              }
              setDragOverColumn((current) =>
                current === status ? null : current
              );
              setDropIndex(null);
              autoScroll.current = { el: null, speed: 0 };
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(status);
            }}
          >
            <ColumnHeader $status={status}>
              <h2>{TaskStatusLabel[status]}</h2>
              <span>{board[status].length}</span>
            </ColumnHeader>

            <CardList data-card-list>
              {board[status].map((task, index) => {
                const isTargetColumn = dragOverColumn === status;
                const isDragged = draggedTask?.task_id === task.task_id;
                const dropsAtEnd = dropIndex === board[status].length;

                const isGroup = task.task_scope === TypeTaskScope.GROUP;
                const assignees = task.assignees ?? [];
                const isMine = assignees.some(
                  (a) => a.user_id === Number(user_id)
                );

                return (
                <Card
                  key={task.task_id}
                  data-task-card={task.task_id}
                  $status={status}
                  $group={isGroup}
                  $dragActive={!!draggedTask}
                  $dragging={isDragged}
                  // abre o vão: todo card a partir do destino desce um pouco
                  $shifted={
                    isTargetColumn &&
                    !isDragged &&
                    dropIndex !== null &&
                    index >= dropIndex
                  }
                  $dropHint={
                    !isTargetColumn || isDragged
                      ? null
                      : dropIndex === index
                      ? "before"
                      : dropsAtEnd && index === board[status].length - 1
                      ? "after"
                      : null
                  }
                  draggable
                  onDragStart={() => setDraggedTask(task)}
                  onDragEnd={clearDragState}
                >
                  <CardTop>
                    <CardIndex $status={status}>{index + 1}</CardIndex>
                    {/* título e descrição ficam em uma linha só, com
                        reticências, para o card ser compacto; o texto inteiro
                        está no tooltip e no modal do olhinho */}
                    <h3 title={task.task_title}>{task.task_title}</h3>

                    {isGroup &&
                      (assignees.length > 0 ? (
                        <AssigneeStack
                          title={assignees
                            .map((a) => a.TB_USER.user_name)
                            .join(", ")}
                        >
                          {assignees.slice(0, 3).map((a) => (
                            <AssigneeDot
                              key={a.task_assignee_id}
                              $self={a.user_id === Number(user_id)}
                            >
                              {initialsOf(a.TB_USER.user_name)}
                            </AssigneeDot>
                          ))}
                          {assignees.length > 3 && (
                            <AssigneeDot>+{assignees.length - 3}</AssigneeDot>
                          )}
                        </AssigneeStack>
                      ) : (
                        <FreeTag title="Ninguém pegou esta tarefa ainda">
                          Livre
                        </FreeTag>
                      ))}
                  </CardTop>

                  {task.task_description && (
                    <p title={task.task_description}>{task.task_description}</p>
                  )}

                  <CardFooter>
                    <span className="due">
                      <FiClock /> {formatDue(task.task_due_date)}
                    </span>

                    <span className="actions">
                      {isGroup && (
                        <CustomTooltip
                          title={
                            isMine
                              ? "Devolver ao time"
                              : "Pegar para fazer"
                          }
                        >
                          <ActionButton
                            onClick={() => handleToggleClaim(task, isMine)}
                          >
                            {isMine ? (
                              <FiUserMinus size={14} color="#fb7185" />
                            ) : (
                              <FiUserPlus size={14} color="#2b7bff" />
                            )}
                          </ActionButton>
                        </CustomTooltip>
                      )}

                      {status === TypeTaskStatus.DONE && canApprove && (
                        <CustomTooltip title="Aprovar Tarefa">
                          <ActionButton onClick={() => handleApprove(task)}>
                            <FiThumbsUp size={14} color="#34d399" />
                          </ActionButton>
                        </CustomTooltip>
                      )}

                      <CustomTooltip title="Ver Tarefa">
                        <ActionButton onClick={() => setTaskToView(task)}>
                          <FiEye size={14} color="#2b7bff" />
                        </ActionButton>
                      </CustomTooltip>

                      <CustomTooltip title="Editar Tarefa">
                        <ActionButton onClick={() => handleOpenEditModal(task)}>
                          <FiEdit size={14} color="#4caf50" />
                        </ActionButton>
                      </CustomTooltip>

                      <CustomTooltip title="Excluir Tarefa">
                        <ActionButton onClick={() => setTaskToDelete(task)}>
                          <FiTrash2 size={14} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    </span>
                  </CardFooter>
                </Card>
                );
              })}

              {board[status].length === 0 && (
                <EmptyColumn $active={dragOverColumn === status}>
                  Arraste uma tarefa para cá
                </EmptyColumn>
              )}
            </CardList>
          </Column>
        ))}
      </Board>
      )}

      <TaskCreateModal
        open={isCreateOpen}
        onClose={handleCloseCreateModal}
        task={selectedTask}
      />

      <TaskViewModal
        open={!!taskToView}
        onClose={() => setTaskToView(null)}
        task={taskToView}
      />

      <TaskCompletedModal
        open={isCompletedOpen}
        onClose={() => setIsCompletedOpen(false)}
        onChanged={refreshAll}
        companyId={isAdmin ? selectedCompany?.company_id : undefined}
      />

      <TaskOverdueModal
        open={isOverdueOpen}
        onClose={() => {
          setIsOverdueOpen(false);
          refreshAll();
        }}
        onChanged={refreshAll}
        companyId={isAdmin ? selectedCompany?.company_id : undefined}
      />

      <ConfirmDialog
        open={!!taskToDelete}
        title="Excluir Tarefa"
        description={`A tarefa "${taskToDelete?.task_title ?? ""}" será apagada de vez. Deseja continuar?`}
        confirmText="Excluir"
        onClose={() => setTaskToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};
